import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, ChevronDown, ChevronRight, Sparkles } from 'lucide-react'
import { navigationData } from '../utils/navigation'
import Logo from './Logo'

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(null)
  const [mobileSubMenu, setMobileSubMenu] = useState(null)
  const dropdownRef = useRef(null)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setMobileMenuOpen(false)
    setActiveDropdown(null)
    setMobileSubMenu(null)
  }, [location])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const navItems = Object.keys(navigationData)

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-void-950/90 backdrop-blur-xl border-b border-void-800/50 shadow-lg shadow-void-950/50'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <Logo className="h-9 w-9 transition-transform group-hover:scale-105" />
            <div className="flex flex-col">
              <span className="font-display font-bold text-lg text-void-50 tracking-tight">
                SynthetIQ
              </span>
              <span className="text-[10px] text-synth-400 font-medium tracking-widest uppercase -mt-1">
                Signals®
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-0.5 xl:gap-1" ref={dropdownRef}>
            {navItems.map((key) => {
              const item = navigationData[key]
              const isActive = location.pathname.startsWith(item.href)
              
              return (
                <div
                  key={key}
                  className="relative"
                  onMouseEnter={() => setActiveDropdown(key)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    to={item.href}
                    className={`flex items-center gap-1 px-3 xl:px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors duration-200 rounded-lg ${
                      isActive
                        ? 'text-synth-400'
                        : 'text-void-300 hover:text-void-50 hover:bg-void-800/50'
                    }`}
                  >
                    {item.title}
                    <ChevronDown className={`hidden xl:block w-4 h-4 transition-transform duration-200 ${
                      activeDropdown === key ? 'rotate-180' : ''
                    }`} />
                  </Link>

                  {/* Mega Menu Dropdown */}
                  <AnimatePresence>
                    {activeDropdown === key && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-1/2 -translate-x-1/2 pt-4"
                      >
                        <div className="bg-void-900/95 backdrop-blur-xl rounded-2xl border border-void-700/50 shadow-2xl shadow-void-950/80 overflow-hidden min-w-[600px]">
                          {/* Header */}
                          <div className="px-6 py-4 border-b border-void-800/50 bg-gradient-to-r from-synth-900/20 to-transparent">
                            <p className="text-xs font-medium text-synth-400 uppercase tracking-wider">
                              {item.title}
                            </p>
                            <p className="text-sm text-void-400 mt-1">{item.description}</p>
                          </div>
                          
                          {/* Categories Grid */}
                          <div className="grid grid-cols-3 gap-0 divide-x divide-void-800/50">
                            {item.categories.map((category) => (
                              <div key={category.name} className="p-4">
                                <Link
                                  to={category.href}
                                  className="text-xs font-semibold text-void-400 uppercase tracking-wider hover:text-synth-400 transition-colors"
                                >
                                  {category.name}
                                </Link>
                                <ul className="mt-3 space-y-1">
                                  {category.items.map((subItem) => (
                                    <li key={subItem.name}>
                                      <Link
                                        to={subItem.href}
                                        className="group block p-2 rounded-lg hover:bg-void-800/50 transition-colors"
                                      >
                                        <span className="text-sm text-void-200 group-hover:text-void-50 transition-colors">
                                          {subItem.name}
                                        </span>
                                        <p className="text-xs text-void-500 mt-0.5 group-hover:text-void-400 transition-colors">
                                          {subItem.description}
                                        </p>
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-2 xl:gap-3 whitespace-nowrap">
            <a href="https://23controls.com/login" className="btn-ghost text-sm btn-primary">
              Sign In
            </a>
            <a href="https://23controls.com/signup" className="btn-primary text-sm flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Get Started
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-void-300 hover:text-void-50 transition-colors"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-void-950/98 backdrop-blur-xl border-b border-void-800/50 overflow-hidden"
          >
            <div className="max-h-[calc(100vh-4rem)] overflow-y-auto">
              <div className="px-4 py-4 space-y-1">
                {navItems.map((key) => {
                  const item = navigationData[key]
                  const isOpen = mobileSubMenu === key
                  
                  return (
                    <div key={key} className="border-b border-void-800/30 last:border-0">
                      <button
                        onClick={() => setMobileSubMenu(isOpen ? null : key)}
                        className="w-full flex items-center justify-between p-3 text-void-200 hover:text-void-50 transition-colors"
                      >
                        <span className="font-medium">{item.title}</span>
                        <ChevronRight className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                      </button>
                      
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="pb-3"
                          >
                            {item.categories.map((category) => (
                              <div key={category.name} className="px-3 py-2">
                                <Link
                                  to={category.href}
                                  className="text-xs font-semibold text-synth-400 uppercase tracking-wider"
                                >
                                  {category.name}
                                </Link>
                                <ul className="mt-2 space-y-1">
                                  {category.items.map((subItem) => (
                                    <li key={subItem.name}>
                                      <Link
                                        to={subItem.href}
                                        className="block py-2 px-3 text-sm text-void-400 hover:text-void-200 hover:bg-void-800/30 rounded-lg transition-colors"
                                      >
                                        {subItem.name}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )
                })}
              </div>
              
              {/* Mobile CTA */}
              <div className="px-4 py-4 border-t border-void-800/50 space-y-3">
                <a href="https://23controls.com/login" className="block w-full text-center btn-secondary">
                  Sign In
                </a>
                <a href="https://23controls.com/signup" className="block w-full text-center btn-primary">
                  Get Started
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
